import {compact, isEqual, isUndefined, omitBy} from 'lodash';

import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {hasProperty} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {onlyItem} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {parseTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {isShallowEqual} from '../../../../helpers/reselectHelpers';
import {ConversationSortPropertiesEnum} from '../../../../models/api/conversationCommonTypes';
import {CustomViewModel} from '../../../../models/api/customViewModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {TeammatesFoldersEnum, TeammatesMessageFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {ResourceMap} from '../../../appTypes';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesParametersActivity,
  InboxesParametersTypesEnum,
  InboxesSearchParameters,
  InboxesSelectionTypesEnum,
} from './inboxesParameters';

export function asConversationsParameters(parameters: InboxesParameters) {
  return parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES ? parameters : undefined;
}

export function asTeammateMessagesParameters(parameters: InboxesParameters) {
  return parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES ? parameters : undefined;
}

/** Find the ID of the selected inbox in the specified inboxes parameters, if any. */
export function findParametersInboxId(parameters: InboxesParameters) {
  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return parameters.inboxId;

    default:
      return undefined;
  }
}

export function findParametersViewId(parameters: InboxesParameters) {
  if (
    parameters.type !== InboxesParametersTypesEnum.VIEW &&
    parameters.type !== InboxesParametersTypesEnum.TEAMMATE_VIEW
  ) {
    return undefined;
  }

  return parameters.viewId;
}

export function findParametersSearch(parameters: InboxesParameters) {
  if (!hasSearchInInboxesParameters(parameters)) {
    return undefined;
  }

  return parameters.search;
}

export function findSearchQueryInParameters(parameters: InboxesParameters) {
  // This only applies to conversation list parameters.
  if (!hasSearchInInboxesParameters(parameters)) {
    return '';
  }

  // Extract the search query from the parameters, append a space to tokenize.
  return `${parameters.search.query} `;
}

export function findAroundActivityId(activity: InboxesParametersActivity | undefined) {
  if (!activity || !hasProperty(activity, 'id')) {
    return undefined;
  }

  return activity.id;
}

export function findParametersActivityId({activity}: InboxesParameters) {
  return findAroundActivityId(activity);
}

export function findAroundActivityDate(activity: InboxesParametersActivity | undefined) {
  if (!activity || !hasProperty(activity, 'date')) {
    return undefined;
  }

  return activity.date;
}

export function findParametersActivityDate({activity}: InboxesParameters) {
  return findAroundActivityDate(activity);
}

export enum CellsSortModesEnum {
  TRACKER = 'TRACKER',
  ORDER = 'ORDER',
  MESSAGE = 'MESSAGE',
}

export function findParametersCellsSortMode(parameters: InboxesParameters) {
  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAM_LENS:
    case InboxesParametersTypesEnum.TEAMMATE_LENS:
    case InboxesParametersTypesEnum.TEAMMATE:
    case InboxesParametersTypesEnum.TEAM:
    case InboxesParametersTypesEnum.GLOBAL:
      return CellsSortModesEnum.ORDER;

    case InboxesParametersTypesEnum.TEAMMATE_MESSAGES:
      return CellsSortModesEnum.MESSAGE;

    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
    case InboxesParametersTypesEnum.USER: {
      const {sortProperty, search} = parameters;
      return sortProperty === ConversationSortPropertiesEnum.BUMPED_AT && !search
        ? CellsSortModesEnum.TRACKER
        : CellsSortModesEnum.ORDER;
    }

    default:
      throw new UnreachableError(parameters);
  }
}

export function findParametersTeammateId(
  userId: number,
  inboxes: ResourceMap<InboxModel>,
  customViews: ResourceMap<CustomViewModel>,
  p: InboxesParameters,
) {
  const delegatedTeammateId = p.type === InboxesParametersTypesEnum.USER && p.teammateId;

  const teammateInboxId = p.type === InboxesParametersTypesEnum.TEAMMATE_INBOX && p.inboxId;
  const teammateInbox = teammateInboxId && inboxes[teammateInboxId];
  const teammateIdFromInbox = teammateInbox && parseTeammateNamespace(teammateInbox.namespace);

  const teammateViewId = findParametersViewId(p);
  const teammateView = teammateViewId && customViews[teammateViewId];
  const teammateIdFromView = teammateView && parseTeammateNamespace(teammateView.namespace);

  return delegatedTeammateId || teammateIdFromInbox || teammateIdFromView || userId;
}

/** Find the corresponding conversation folder for the specified message folder. */
export function messageFolderToConversationFolder(
  messageFolder: TeammatesMessageFoldersEnum,
  defaultFolder = TeammatesFoldersEnum.INBOX,
): TeammatesFoldersEnum {
  if (messageFolder === TeammatesMessageFoldersEnum.SENT) {
    return TeammatesFoldersEnum.SENT;
  }

  return defaultFolder;
}

/** Picks the inboxes parameters that identify the current cells (P2) context. */
export function pickCellsParameters(parameters: InboxesParameters) {
  const searchParameters = hasSearchInInboxesParameters(parameters)
    ? pickCellsSearchParameters(parameters.search)
    : undefined;

  // - Omit undefined property values, and treat them the same as missing properties.
  // - Omit the P2 selection.
  // - Omit the P3 focused activity.
  return omitBy(
    {
      ...parameters,
      search: searchParameters,
    },
    (value, key) => isUndefined(value) || key === 'selection' || key === 'activity',
  );
}

/** Picks the inboxes search parameters that are relevant to the current cells (P2) context. */
function pickCellsSearchParameters(parameters: InboxesSearchParameters) {
  // - Omit undefined property values, and treat them the same as missing properties.
  return omitBy(parameters, (value, key) => isUndefined(value));
}

/** Whether the parameters represent the same cells panel. */
export function areParametersCellsEqual(a: InboxesParameters, b: InboxesParameters): boolean {
  return isEqual(pickCellsParameters(a), pickCellsParameters(b));
}

export interface InboxesNormalSelection<T> {
  readonly type: InboxesSelectionTypesEnum.NORMAL;
  readonly selectedIds: ReadonlyArray<T>;
}

export interface InboxesAllExceptSelection<T> {
  readonly type: InboxesSelectionTypesEnum.ALL_EXCEPT;
  readonly deselectedIds: ReadonlyArray<T>;
}

export type InboxesSelection<T> = InboxesNormalSelection<T> | InboxesAllExceptSelection<T>;

export type InboxesSelectionIdType = string | number;

export const inboxesParametersSelectAllKeyword = 'all';

/** Type guard for selections to determine if it is in the select all state or not. */
export function computeIsAllExceptSelection(
  selection: InboxesSelection<unknown>,
): selection is InboxesAllExceptSelection<unknown> {
  return selection.type === InboxesSelectionTypesEnum.ALL_EXCEPT && selection.deselectedIds !== undefined;
}

export function computeIsNormalSelection(
  selection: InboxesSelection<unknown>,
): selection is InboxesNormalSelection<unknown> {
  return selection.type === InboxesSelectionTypesEnum.NORMAL && selection.selectedIds !== undefined;
}

export function findOnlySelectedId<T>(selection: InboxesSelection<T>): T | undefined {
  if (computeIsAllExceptSelection(selection)) {
    return undefined;
  }

  return onlyItem(selection.selectedIds);
}

export function computeIsExactlyOneIdSelected(selection: InboxesSelection<unknown>): boolean {
  return computeIsNormalSelection(selection) && compact(selection.selectedIds).length === 1;
}

export function findFirstSelectedId<T>(selection: InboxesSelection<T>): T | undefined {
  if (computeIsAllExceptSelection(selection) || selection.selectedIds.length === 0) {
    return undefined;
  }

  return selection.selectedIds[0];
}

export function computeIsEmptyInboxesSelection(selection: InboxesSelection<unknown>): boolean {
  return computeIsNormalSelection(selection) && selection.selectedIds.length === 0;
}

export const emptyInboxesSelection = Object.freeze(normalInboxesSelection<any>([]));

export function normalInboxesSelection<T>(selectedIds: ReadonlyArray<T>): InboxesNormalSelection<T> {
  return {
    type: InboxesSelectionTypesEnum.NORMAL,
    selectedIds,
  };
}

export function allExceptInboxesSelection<T>(deselectedIds: ReadonlyArray<T>): InboxesAllExceptSelection<T> {
  return {
    type: InboxesSelectionTypesEnum.ALL_EXCEPT,
    deselectedIds,
  };
}

export function computeIsIdSelected<T>(selection: InboxesSelection<T>, id: T): boolean {
  if (computeIsAllExceptSelection(selection)) {
    return !selection.deselectedIds.includes(id);
  }

  return selection.selectedIds.includes(id);
}

export function computeIsEqualSelection<T>(a: InboxesSelection<T>, b: InboxesSelection<T>): boolean {
  if (computeIsAllExceptSelection(a) && computeIsAllExceptSelection(b)) {
    return isShallowEqual(a.deselectedIds, b.deselectedIds);
  }

  if (computeIsNormalSelection(a) && computeIsNormalSelection(b)) {
    return isShallowEqual(a.selectedIds, b.selectedIds);
  }

  return false;
}

export function computeIsMultipleIdsSelected(selection: InboxesSelection<unknown>): boolean {
  if (computeIsAllExceptSelection(selection)) {
    return true;
  }
  return selection.selectedIds.length > 1;
}

export function computeSelectedIdsForSelection<T>(
  selection: InboxesSelection<T>,
  conversationIds: ReadonlyArray<T>,
): ReadonlyArray<T> {
  if (computeIsAllExceptSelection(selection)) {
    // The case of a select all that is not a bulk request is handled here.
    return conversationIds;
  }
  return selection.selectedIds;
}
